import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { SoundTogglePanel } from '../../ui/SoundTogglePanel/SoundTogglePanel';
import clsx from 'clsx';

import { BurgerMenu } from 'shared/components/sections/burger-menu';
import PrimaryButton from 'shared/components/ui/PrimaryButton/PrimaryButton';
import SecondaryBtn from 'shared/components/ui/SecondaryBtn/SecondaryBtn';
import { OptimizedImage } from 'shared/components/ui/optimized-image/optimized-image';
import { ROUTES } from 'shared/constants';
import { useAccount, useConnect } from 'shared/hooks/use-web3';
import { useUIStore } from 'shared/store/uiStore';
import { StackName, useWeb3ClientsStore } from 'shared/store/web3-clients-store';
import { isMocked } from 'shared/utils/isMocked';

import ArrowLeftIcon from 'assets/arrow-left.svg?react';
import ControllerIcon from 'assets/controller-brown.svg?react';
import logo from 'assets/png/logo.png?format=webp;avif&as=picture';
import WalletIcon from 'assets/wallet-brown.svg?react';

interface IHeaderProps {
  title?: React.ReactNode;
  className?: string;
  showMenu?: boolean;
  isGuestMode?: boolean;
}

export const Header = ({
  title,
  className,
  showMenu = true,
  isGuestMode = false,
}: IHeaderProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const { isConnected } = useAccount();

  const { connect, isPending } = useConnect();
  const { setConnectWalletModalOpen } = useUIStore();

  const handleRedirectToHome = useCallback(() => navigate(ROUTES.HOME), [navigate]);
  const handleRedirectToExplore = useCallback(() => navigate(ROUTES.EXPLORE_GAMES), [navigate]);
  const handleRedirectToLobby = useCallback(() => navigate(ROUTES.LOBBY), [navigate]);

  const shouldShowConnectWallet = useMemo(() => !isMocked && !isConnected, [isConnected]);

  const handleConnectWeb3 = useCallback(
    () => setConnectWalletModalOpen(true),
    [setConnectWalletModalOpen],
  );

  const { stack } = useWeb3ClientsStore();

  return (
    <header
      className={clsx('flex w-full items-center justify-between md:grid md:grid-cols-3', className)}
    >
      <div className="flex items-center gap-4 sm:gap-6">
        <button onClick={handleRedirectToHome} className="h-[36px] lg:h-[56px]">
          <OptimizedImage
            imageData={logo}
            alt="rock paper scissors logo"
            className="w-[36px] cursor-pointer lg:w-[56px]"
          />
        </button>

        <div className="hidden h-[56px] w-[3px] bg-indigo lg:flex" />

        {location.pathname.startsWith(ROUTES.STREAM) && !isGuestMode && (
          <SecondaryBtn
            text="Back to streams"
            svg={<ArrowLeftIcon className="h-5 w-5 sm:h-6 sm:w-6" />}
            onClick={handleRedirectToExplore}
            className="hidden lg:block"
          />
        )}
      </div>

      <div className="gradient-title flex items-center justify-center gap-[8px] text-[16px] font-bold sm:text-[30px] lg:text-[40px]">
        {title}
      </div>

      <div className="flex items-center justify-end gap-2 sm:gap-6">
        {isConnected &&
          showMenu &&
          location.pathname !== ROUTES.LOBBY &&
          !location.pathname.startsWith(ROUTES.ROOM) && (
            <PrimaryButton
              onClick={handleRedirectToLobby}
              text="pages.liveGamesSection.create"
              svg={<ControllerIcon className="h-5 w-5 sm:h-6 sm:w-6" />}
              isAnimated
              className="hidden lg:block"
            />
          )}

        <SoundTogglePanel />

        <div className="hidden h-[56px] w-[3px] bg-indigo lg:flex" />

        {shouldShowConnectWallet && (
          <PrimaryButton
            onClick={() => {
              return stack === StackName.PRIVY ? connect() : handleConnectWeb3();
            }}
            text="common.connectWallet"
            data-testid="connectWallet"
            svg={<WalletIcon className="h-5 w-5 sm:h-6 sm:w-6" />}
            isAnimated
            disabled={isPending}
            className="hidden sm:block"
          />
        )}

        {showMenu && <BurgerMenu />}
      </div>
    </header>
  );
};
